/**
 * Quantity Breaks UI Enhancement for Horizon Theme
 * 
 * ENHANCEMENT ONLY - No selection logic
 * Uses native Horizon inputs/labels - only enhances visual display
 */

// ========================
// A) DEBUG TOGGLE
// ========================
const QB_DEBUG = new URLSearchParams(location.search).has('qb_debug') || localStorage.getItem('qb_debug') === '1';
const qbLog = (...args) => QB_DEBUG && console.log('[QB]', ...args);
const qbError = (...args) => console.error('[QB ERROR]', ...args);
const qbWarn = (...args) => console.warn('[QB WARN]', ...args);

// Weight mode: grams per cup for cost-per-cup calculation (الوزن)
const GRAMS_PER_CUP = 7;
// Largest tier gets "Most practical" badge when cups >= this
const CUPS_MOST_PRACTICAL_THRESHOLD = 60;

// Always log script load
console.log('[QB] Quantity Breaks script loaded. Debug mode:', QB_DEBUG ? 'ON' : 'OFF (add ?qb_debug=1 to URL)');

/**
 * Parse grams from option value label (e.g. "25 g", "40g", "100 غ", "25 grams").
 * Returns first number (int or decimal) or null.
 * @param {string} valueLabel
 * @returns {number|null}
 */
function parseGrams(valueLabel) {
  if (!valueLabel || typeof valueLabel !== 'string') return null;
  const s = valueLabel.trim().replace(/,/g, '.');
  const match = s.match(/(\d+(?:\.\d+)?)/);
  if (!match) return null;
  const num = parseFloat(match[1], 10);
  return Number.isFinite(num) && num > 0 ? num : null;
}

/** Arabic fallbacks for usage labels when translations missing */
const USAGE_LABELS_AR = {
  quick_try: 'تجربة سريعة',
  office_travel: 'مكتب/سفر',
  daily_use: 'استخدام يومي',
  family_guests: 'عائلة/ضيافة'
};

/**
 * Usage tier key by grams (for "Best for" / "Ideal for" label).
 * @param {number} grams
 * @returns {string} Translation key: quick_try | office_travel | daily_use | family_guests
 */
function usageLabelForGrams(grams) {
  if (grams <= 30) return 'quick_try';
  if (grams <= 60) return 'office_travel';
  if (grams <= 200) return 'daily_use';
  return 'family_guests';
}

/**
 * Format cups for display as whole numbers only.
 * @param {number} cups
 * @returns {string}
 */
function formatCups(cups) {
  if (!Number.isFinite(cups) || cups <= 0) return '0';
  return String(Math.round(cups));
}

/**
 * Arabic cup word by rounded cup count:
 * 1, 11, and >= 11 -> فنجان
 * otherwise -> فناجين
 * @param {number} cupsRounded
 * @returns {string}
 */
function getCupWord(cupsRounded) {
  if (cupsRounded === 1 || cupsRounded >= 11) return 'فنجان';
  return 'فناجين';
}

function qbDumpEl(el, label) {
  if (!QB_DEBUG || !el) return;
  const cs = getComputedStyle(el);
  qbLog(label, {
    exists: !!el,
    text: el.textContent?.trim(),
    display: cs.display,
    visibility: cs.visibility,
    opacity: cs.opacity,
    position: cs.position,
    zIndex: cs.zIndex,
    className: el.className
  });
}

// ========================
// B) MORPH SETTLE RUNNER
// ========================
function createSettleRunner(container, runFn, isEnhancingFn) {
  let t = null;
  let lastMutationAt = 0;
  let isRunning = false;

  const schedule = () => {
    // Don't schedule if we're already enhancing (prevents loop)
    if (isEnhancingFn && isEnhancingFn()) {
      qbLog('settle skipped - already enhancing');
      return;
    }
    
    lastMutationAt = performance.now();
    clearTimeout(t);
    t = setTimeout(() => {
      const age = performance.now() - lastMutationAt;
      if (age >= 50 && !isRunning) {
        isRunning = true;
        qbLog('settle complete, running enhance');
        runFn();
        // Reset running flag after a delay
        setTimeout(() => { isRunning = false; }, 100);
      } else if (age < 50) {
        qbLog('settle window too short, rescheduling');
        schedule();
      }
    }, 80);
  };

  const mo = new MutationObserver((muts) => {
    // Only react to mutations that look like Horizon morphing, not our own changes
    let shouldReact = false;
    
    for (const mut of muts) {
      // Ignore if we're enhancing (our own changes)
      if (isEnhancingFn && isEnhancingFn()) {
        continue;
      }
      
      // Only react to childList changes (DOM structure changes from Horizon)
      // Ignore characterData changes (text content - we cause these)
      // Ignore attribute changes (class changes - we cause these)
      if (mut.type === 'childList') {
        // Check if it's a significant change (labels added/removed, not just text nodes)
        const hasSignificantChange = mut.addedNodes.length > 0 || mut.removedNodes.length > 0;
        if (hasSignificantChange) {
          // Check if it's a label being added/removed (Horizon morphing)
          const isLabelChange = Array.from(mut.addedNodes).some(node => 
            node.nodeType === 1 && (
              node.classList?.contains('variant-option__button-label--quantity-break') ||
              node.querySelector?.('.variant-option__button-label--quantity-break')
            )
          ) || Array.from(mut.removedNodes).some(node =>
            node.nodeType === 1 && node.classList?.contains('variant-option__button-label--quantity-break')
          );
          
          if (isLabelChange) {
            shouldReact = true;
            break;
          }
        }
      }
    }
    
    if (shouldReact) {
      qbLog('significant morphing detected, scheduling enhance');
      schedule();
    } else if (QB_DEBUG) {
      qbLog('mutations ignored (our own changes or insignificant)', muts.length);
    }
  });

  mo.observe(container, { subtree: true, childList: true, characterData: false, attributes: false });
  // Don't schedule initial - wait for actual morphing
  return () => mo.disconnect();
}

class QuantityBreaksEnhancement {
  constructor(variantPicker) {
    this.variantPicker = variantPicker;
    this.quantityOption = null;
    this.allVariants = [];
    this.productOptions = [];
    this.translations = {};
    this.currency = 'USD';
    this.locale = 'en-US';
    this.isEnhancing = false;
    this.enhanceTimeout = null;
    this.scriptObserver = null;
    this.settleRunner = null;
    this.wipeCheckTimeout = null;
    this.userChangedQuantity = false;
    this.isAutoSelectingTier = false;

    this.init();
  }

  init() {
    try {
      // Find quantity option container
      this.quantityOption = this.variantPicker.querySelector('.variant-option--quantity-breaks');
      if (!this.quantityOption) {
        qbLog('quantity option container not found');
        qbWarn('No .variant-option--quantity-breaks found in variant-picker');
        return;
      }

      qbLog('Found quantity breaks container');

      // Add data attribute for CSS specificity
      this.quantityOption.setAttribute('data-quantity-breaks', '1');

      // Load data from embedded JSON
      this.loadData();

      // Initialize quantity breaks UI enhancement
      this.enhanceQuantityBreaks();

      // Setup settle runner for morph detection
      this.setupSettleRunner();

      // Listen for native variant changes to recalculate prices
      this.setupVariantChangeListener();

      qbLog('Initialization complete');
    } catch (error) {
      qbError('Init error:', error);
      console.error('[QB] Initialization failed:', error);
    }
  }

  loadData() {
    // Load variants
    const variantsScript = this.variantPicker.querySelector('script[data-all-variants]');
    if (variantsScript) {
      try {
        this.allVariants = JSON.parse(variantsScript.textContent);
        qbLog('loaded variants', this.allVariants.length);
      } catch (e) {
        console.error('Failed to parse variants data', e);
      }
    }

    // Load product options structure
    const optionsScript = this.variantPicker.querySelector('script[data-product-options]');
    if (optionsScript) {
      try {
        this.productOptions = JSON.parse(optionsScript.textContent);
        qbLog('loaded product options', this.productOptions.length);
      } catch (e) {
        console.error('Failed to parse product options data', e);
      }
    }

    // Load quantity breaks translations (popular, off, per_serving, save, quantity_option_names)
    const translationsScript = this.variantPicker.querySelector('script[data-quantity-breaks-translations]');
    if (translationsScript) {
      try {
        this.translations = JSON.parse(translationsScript.textContent);
        qbLog('loaded translations', this.translations);
      } catch (e) {
        console.warn('Failed to parse quantity breaks translations', e);
      }
    }
    // Allowed option names for quantity/weight breaks (quantity, الكمية, الوزن, theme translation)
    this.quantityOptionNames = Array.isArray(this.translations?.quantity_option_names)
      ? this.translations.quantity_option_names.map((n) => (typeof n === 'string' ? n.toLowerCase() : ''))
      : ['quantity', 'الوزن'];
    this.quantityOptionNamesRaw = Array.isArray(this.translations?.quantity_option_names)
      ? this.translations.quantity_option_names
      : ['quantity', 'الكمية', 'الوزن'];

    // Get currency and locale
    if (window.Shopify && window.Shopify.locale) {
      this.locale = window.Shopify.locale;
    } else if (document.documentElement.lang) {
      this.locale = document.documentElement.lang;
    }
    
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      this.currency = window.Shopify.currency.active;
    }
    
    // Fallback: try to get from meta tag or data attribute
    const currencyMeta = document.querySelector('meta[name="currency"]') || 
                        document.querySelector('[data-currency]');
    if (currencyMeta) {
      this.currency = currencyMeta.content || currencyMeta.dataset.currency || this.currency;
    }
  }

  // ========================
  // C) ENSURE CARD MARKUP (idempotent)
  // ========================
  ensureCardMarkup(labelEl) {
    if (!labelEl) return;

    // Add card class to label (passive - won't trigger events)
    if (!labelEl.classList.contains('qb__card')) {
      labelEl.classList.add('qb__card');
    }

    // Check and create qb__off
    let offEl = labelEl.querySelector('.qb__off');
    if (!offEl) {
      offEl = document.createElement('span');
      offEl.className = 'qb__off';
      offEl.setAttribute('aria-hidden', 'true');
      // Use appendChild instead of insertBefore to avoid triggering mutations on input
      labelEl.appendChild(offEl);
      qbLog('injected qb__off');
    }

    // Check and create qb__title
    let titleEl = labelEl.querySelector('.qb__title');
    if (!titleEl) {
      titleEl = document.createElement('span');
      titleEl.className = 'qb__title';
      titleEl.setAttribute('aria-hidden', 'true');
      // Insert after input but before other content
      const input = labelEl.querySelector('input[type="radio"]');
      if (input && input.nextSibling) {
        labelEl.insertBefore(titleEl, input.nextSibling);
      } else {
        labelEl.appendChild(titleEl);
      }
      qbLog('injected qb__title');
    }

    // Check and create qb__unit
    let unitEl = labelEl.querySelector('.qb__unit');
    if (!unitEl) {
      unitEl = document.createElement('span');
      unitEl.className = 'qb__unit';
      unitEl.setAttribute('aria-hidden', 'true');
      // Insert after title
      if (titleEl && titleEl.nextSibling) {
        labelEl.insertBefore(unitEl, titleEl.nextSibling);
      } else {
        labelEl.appendChild(unitEl);
      }
      qbLog('injected qb__unit');
    }

    // Check and create qb__subtitle (secondary line, e.g. "Best for: Daily use")
    let subtitleEl = labelEl.querySelector('.qb__subtitle');
    if (!subtitleEl) {
      subtitleEl = document.createElement('span');
      subtitleEl.className = 'qb__subtitle';
      subtitleEl.setAttribute('aria-hidden', 'true');
      if (unitEl && unitEl.nextSibling) {
        labelEl.insertBefore(subtitleEl, unitEl.nextSibling);
      } else {
        labelEl.appendChild(subtitleEl);
      }
      qbLog('injected qb__subtitle');
    }

    // Check and create qb__badge
    let badgeEl = labelEl.querySelector('.qb__badge');
    if (!badgeEl) {
      badgeEl = document.createElement('span');
      badgeEl.className = 'qb__badge';
      badgeEl.setAttribute('aria-hidden', 'true');
      badgeEl.textContent = 'الأكثر مبيعا';
      labelEl.appendChild(badgeEl);
      qbLog('injected qb__badge');
    }

    // Check and create qb__sold-out-badge
    let soldOutEl = labelEl.querySelector('.qb__sold-out-badge');
    if (!soldOutEl) {
      soldOutEl = document.createElement('span');
      soldOutEl.className = 'qb__sold-out-badge';
      soldOutEl.setAttribute('aria-hidden', 'true');
      soldOutEl.textContent = this.translations?.sold_out ?? 'غير متوفر حالياً';
      labelEl.appendChild(soldOutEl);
      qbLog('injected qb__sold-out-badge');
    }
  }

  enhanceQuantityBreaks(retryCount = 0) {
    // Prevent infinite loops - don't run if already enhancing
    if (this.isEnhancing) {
      qbLog('enhance skipped - already enhancing');
      return;
    }
    this.isEnhancing = true;
    
    qbLog('enhanceQuantityBreaks() called, retryCount:', retryCount);

    try {
      // Re-find quantity option in case DOM was morphed
      const newQuantityOption = this.variantPicker.querySelector('.variant-option--quantity-breaks');
      if (!newQuantityOption) {
        qbLog('quantity option not found, retry', retryCount);
        // Retry once if element not found (morphing might be in progress)
        if (retryCount < 2) {
          setTimeout(() => {
            this.isEnhancing = false;
            this.enhanceQuantityBreaks(retryCount + 1);
          }, 50);
        }
        return;
      }
      
      // If quantity option changed (morphing), update observer
      if (newQuantityOption !== this.quantityOption) {
        qbLog('quantity option changed (morphed)');
        if (this.settleRunner) {
          this.settleRunner();
        }
        this.quantityOption = newQuantityOption;
        this.quantityOption.setAttribute('data-quantity-breaks', '1');
        // Re-setup settle runner on new element
        this.setupSettleRunner();
      } else {
        this.quantityOption = newQuantityOption;
      }

      // Get all native labels (which are the cards)
      const labels = this.quantityOption.querySelectorAll('.variant-option__button-label--quantity-break');
      if (labels.length === 0) {
        qbLog('no labels found, retry', retryCount);
        // Retry once if labels not found (morphing might be in progress)
        if (retryCount < 2) {
          setTimeout(() => {
            this.isEnhancing = false;
            this.enhanceQuantityBreaks(retryCount + 1);
          }, 50);
        }
        return;
      }

      qbLog('enhancing', labels.length, 'labels');

      // ========================
      // C) ENSURE MARKUP EXISTS (idempotent)
      // ========================
      labels.forEach(label => {
        this.ensureCardMarkup(label);
      });
      
      qbLog('Ensured markup for', labels.length, 'labels');
      
      // Verify elements exist after ensuring markup
      const firstLabel = labels[0];
      if (firstLabel) {
        const hasOff = !!firstLabel.querySelector('.qb__off');
        const hasTitle = !!firstLabel.querySelector('.qb__title');
        const hasUnit = !!firstLabel.querySelector('.qb__unit');
        const hasSubtitle = !!firstLabel.querySelector('.qb__subtitle');
        const hasBadge = !!firstLabel.querySelector('.qb__badge');
        qbLog('Elements check:', { hasOff, hasTitle, hasUnit, hasSubtitle, hasBadge });
        
        if (!hasOff || !hasTitle || !hasUnit || !hasBadge) {
          qbWarn('Missing QB elements after ensureCardMarkup!', { hasOff, hasTitle, hasUnit, hasBadge });
        }
      }

      // Weight mode (الوزن): option name is الوزن → grams/cups/cost_per_cup. Else quantity mode (n/per-unit).
      const optionName = (this.quantityOption.getAttribute('data-option-name') || '').trim();
      this.isWeightMode = optionName === 'الوزن' || optionName.toLowerCase() === 'weight';

      const tiers = this.isWeightMode
        ? Array.from(labels).map(label => {
            const input = label.querySelector('input[type="radio"]');
            if (!input) return null;
            const valueStr = input.dataset.quantityValue || input.value || '';
            const grams = parseGrams(valueStr);
            if (grams == null) return null;
            const cups = grams / GRAMS_PER_CUP;
            return {
              label,
              input,
              n: cups,
              grams,
              cups,
              value: valueStr,
              optionValueId: input.dataset.optionValueId,
              variantId: input.dataset.variantId,
              available: input.dataset.optionAvailable === 'true'
            };
          }).filter(tier => tier && tier.grams > 0).sort((a, b) => a.grams - b.grams)
        : Array.from(labels).map(label => {
            const input = label.querySelector('input[type="radio"]');
            if (!input) return null;
            const valueStr = input.dataset.quantityValue || input.value || '';
            const match = valueStr.match(/(\d+)/);
            const n = match ? Math.max(1, parseInt(match[1], 10)) : 0;
            return {
              label,
              input,
              n,
              value: valueStr,
              optionValueId: input.dataset.optionValueId,
              variantId: input.dataset.variantId,
              available: input.dataset.optionAvailable === 'true'
            };
          }).filter(tier => tier && tier.n > 0).sort((a, b) => a.n - b.n);

      if (tiers.length === 0) {
        qbLog('no valid tiers found');
        return;
      }

      qbLog('found tiers:', this.isWeightMode ? tiers.map(t => t.grams) : tiers.map(t => t.n));

      // Weight mode: best cost-per-cup = min of all cost_per_cup (best value tier)
      let bestCostPerCup = Infinity;
      if (this.isWeightMode && tiers.length > 0) {
        tiers.forEach((tier) => {
          const variant = this.findVariantForTier(tier);
          if (variant && tier.cups > 0) {
            const costPerCup = variant.price / tier.cups;
            if (costPerCup < bestCostPerCup) bestCostPerCup = costPerCup;
          }
        });
        if (bestCostPerCup === Infinity) bestCostPerCup = 0;
        qbLog('weight mode bestCostPerCup:', bestCostPerCup);
      }

      // Mark popular tier (one-before-last, only if 3+ tiers)
      // First, remove popular class from all labels
      labels.forEach(label => {
        label.classList.remove('qb__has-popular');
        const badge = label.querySelector('.qb__badge');
        if (badge) {
          badge.classList.remove('qb__badge--visible', 'qb__badge--most-practical');
        }
        const soldOutBadge = label.querySelector('.qb__sold-out-badge');
        if (soldOutBadge) {
          soldOutBadge.classList.remove('qb__sold-out-badge--visible');
        }
      });
      
      let popularTier = null;
      if (tiers.length >= 3) {
        // Weight mode: middle tier (median); quantity mode: second-to-last
        const popularIndex = this.isWeightMode
          ? Math.floor(tiers.length / 2)
          : tiers.length - 2;
        popularTier = tiers[popularIndex];
        const popularLabel = popularTier.label;

        if (popularTier.available) {
          popularLabel.classList.add('qb__has-popular');

          // Ensure badge is visible using class; content = "Popular"
          const badge = popularLabel.querySelector('.qb__badge');
          if (badge) {
            badge.textContent = 'الأكثر مبيعا';
            badge.classList.add('qb__badge--visible');
            badge.classList.remove('qb__badge--most-practical');
            qbLog('set popular badge on tier', this.isWeightMode ? popularTier.grams : popularTier.n, 'at index', popularIndex);
          } else {
            qbLog('WARNING: popular badge element not found');
            qbWarn('Popular badge element missing!');
          }
        }
      } else {
        qbLog('not enough tiers for popular badge', tiers.length);
      }

      // Always preselect the best-selling tier when available.
      // Fallback to first available tier if the popular one is unavailable.
      const selectedTier = tiers.find((tier) => tier && tier.input && tier.input.checked) || null;
      const preselectTier = (popularTier && popularTier.available)
        ? popularTier
        : tiers.find((tier) => tier.available) || null;
      const shouldAutoSelect = !this.userChangedQuantity
        && preselectTier
        && preselectTier.input
        && (!selectedTier || selectedTier.input.dataset.optionValueId !== preselectTier.input.dataset.optionValueId);
      if (shouldAutoSelect) {
        this.isAutoSelectingTier = true;
        preselectTier.input.checked = true;
        preselectTier.input.dispatchEvent(new Event('change', { bubbles: true }));
        if (window.matchMedia('(max-width: 749px)').matches) {
          requestAnimationFrame(() => {
            preselectTier.label.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
          });
        }
        this.isAutoSelectingTier = false;
        qbLog('preselected tier', this.isWeightMode ? preselectTier.grams : preselectTier.n);
      }

      // Weight mode: optionally mark largest tier as "Most practical" when cups >= threshold
      let mostPracticalTier = null;
      if (this.isWeightMode && tiers.length > 0) {
        const largest = tiers[tiers.length - 1];
        if (largest.cups >= CUPS_MOST_PRACTICAL_THRESHOLD) {
          mostPracticalTier = largest;
          const badge = mostPracticalTier.label.querySelector('.qb__badge');
          if (badge && mostPracticalTier !== popularTier && mostPracticalTier.available) {
            badge.textContent = this.translations?.usage?.most_practical ?? this.translations?.most_practical ?? 'الأكثر عملية';
            badge.classList.add('qb__badge--visible', 'qb__badge--most-practical');
          }
        }
      }

      // Calculate base per-unit price (smallest tier) or best cost-per-cup (weight mode)
      const baseTier = tiers[0];
      const baseVariant = this.findVariantForTier(baseTier);
      const basePerUnit = this.isWeightMode
        ? (baseVariant && baseTier.cups > 0 ? baseVariant.price / baseTier.cups : 0)
        : (baseVariant ? baseVariant.price / baseTier.n : 0);

      qbLog('base tier:', this.isWeightMode ? baseTier.grams : baseTier.n, 'basePerUnit:', basePerUnit);

      // Update each tier with pricing info
      tiers.forEach(tier => {
        const variant = this.findVariantForTier(tier);

        if (variant) {
          const perUnit = this.isWeightMode && tier.cups > 0
            ? variant.price / tier.cups
            : variant.price / tier.n;
          const savingsPct = this.isWeightMode
            ? (bestCostPerCup > 0 && perUnit > 0 ? Math.max(0, Math.round((1 - bestCostPerCup / perUnit) * 100)) : 0)
            : (basePerUnit > 0 ? Math.max(0, Math.round((1 - perUnit / basePerUnit) * 100)) : 0);
          const unitWord = this.getUnitFromOptionValue(tier.value);

          // Update card content elements - use class selectors
          const discountEl = tier.label.querySelector('.qb__off');
          const titleEl = tier.label.querySelector('.qb__title');
          const unitEl = tier.label.querySelector('.qb__unit');
          const subtitleEl = tier.label.querySelector('.qb__subtitle');

          // Discount badge removed: never show "-x% OFF"
          if (discountEl) {
            discountEl.textContent = '';
            discountEl.classList.remove('qb__off--visible');
          }

          if (titleEl) {
            let titleText = this.isWeightMode ? tier.value : `${tier.n} ${unitWord}`;
            if (this.isWeightMode && titleText) {
              titleText = String(titleText).replace(/(\d)\s*g\b/gi, '$1 جرام').replace(/\bg\b/gi, 'جرام');
            }
            titleEl.textContent = titleText;
          }

          if (unitEl) {
            if (this.isWeightMode) {
              const approxPrefix = '≈ ';
              const cupsFormatted = formatCups(tier.cups);
              const cupsRounded = Number(cupsFormatted);
              const cupsSuffix = getCupWord(cupsRounded);
              unitEl.textContent = `${approxPrefix}${cupsFormatted} ${cupsSuffix}`.trim();
            } else {
              const unitText = `${this.formatMoneyAmountFirst(perUnit)} / ${this.getUnitSingular(unitWord)}`;
              unitEl.textContent = unitText;
            }
          }

          if (subtitleEl) {
            if (this.isWeightMode) {
              const usageKey = usageLabelForGrams(tier.grams);
              const usageText = USAGE_LABELS_AR[usageKey] || usageKey;
              const bestForPrefix = 'مناسب ل';
              subtitleEl.textContent = bestForPrefix + usageText;
              subtitleEl.classList.add('qb__subtitle--visible');
            } else {
              subtitleEl.textContent = '';
              subtitleEl.classList.remove('qb__subtitle--visible');
            }
          }
        } else {
          qbLog('no variant found for tier', this.isWeightMode ? tier.grams : tier.n);
        }

        const soldOutEl = tier.label.querySelector('.qb__sold-out-badge');
        if (soldOutEl) {
          if (!tier.available) {
            soldOutEl.textContent = this.translations?.sold_out ?? 'غير متوفر حالياً';
            soldOutEl.classList.add('qb__sold-out-badge--visible');
            tier.label.classList.remove('qb__has-popular');
            const promoBadge = tier.label.querySelector('.qb__badge');
            if (promoBadge) {
              promoBadge.classList.remove('qb__badge--visible', 'qb__badge--most-practical');
            }
          } else {
            soldOutEl.classList.remove('qb__sold-out-badge--visible');
          }
        }
      });

      // ========================
      // D) TARGETED LOGS
      // ========================
      // Log details for selected + popular tier
      const selectedLabel = this.quantityOption.querySelector('.variant-option__button-label--quantity-break:has(input:checked)');
      const representativeLabel = popularTier ? popularTier.label : (selectedLabel || labels[0]);

      if (representativeLabel) {
        qbLog('=== REPRESENTATIVE LABEL DEBUG ===');
        qbLog('label textContent:', representativeLabel.textContent?.trim());
        
        const offEl = representativeLabel.querySelector('.qb__off');
        const badgeEl = representativeLabel.querySelector('.qb__badge');
        const titleEl = representativeLabel.querySelector('.qb__title');
        const unitEl = representativeLabel.querySelector('.qb__unit');

        qbDumpEl(offEl, 'offEl');
        qbDumpEl(badgeEl, 'badgeEl');
        qbDumpEl(titleEl, 'titleEl');
        qbDumpEl(unitEl, 'unitEl');

        if (popularTier && representativeLabel === popularTier.label) {
          qbLog('popular tier:', {
            n: popularTier.n,
            hasPopularClass: representativeLabel.classList.contains('qb__has-popular'),
            badgeVisible: badgeEl?.classList.contains('qb__badge--visible')
          });
        }

        const variant = this.findVariantForTier(tiers.find(t => t.label === representativeLabel));
        if (variant) {
          const basePerUnit = tiers[0] ? (this.findVariantForTier(tiers[0])?.price / tiers[0].n || 0) : 0;
          const perUnit = variant.price / (tiers.find(t => t.label === representativeLabel)?.n || 1);
          const savingsPct = basePerUnit > 0 ? Math.max(0, Math.round((1 - perUnit / basePerUnit) * 100)) : 0;
          qbLog('computed savingsPct:', savingsPct, 'for tier', tiers.find(t => t.label === representativeLabel)?.n);
          qbLog('offEl visible class:', offEl?.classList.contains('qb__off--visible'));
        }
      }

      // ========================
      // D) WIPE CHECK + RETRY IF TEXT EMPTY
      // ========================
      // Check if Horizon wipes qb spans after enhance, and retry if text is empty
      if (this.wipeCheckTimeout) {
        clearTimeout(this.wipeCheckTimeout);
      }
      this.wipeCheckTimeout = setTimeout(() => {
        const checkLabels = this.quantityOption?.querySelectorAll('.variant-option__button-label--quantity-break');
        if (checkLabels) {
          let wipedCount = 0;
          let emptyTextCount = 0;
          checkLabels.forEach(label => {
            const hasOff = label.querySelector('.qb__off');
            const hasTitle = label.querySelector('.qb__title');
            const hasUnit = label.querySelector('.qb__unit');
            const hasBadge = label.querySelector('.qb__badge');
            if (!hasOff || !hasTitle || !hasUnit || !hasBadge) {
              wipedCount++;
            }
            // Check if text content is empty (Horizon might have cleared it)
            if (hasTitle && !hasTitle.textContent.trim()) {
              emptyTextCount++;
            }
            if (hasUnit && !hasUnit.textContent.trim()) {
              emptyTextCount++;
            }
          });
          
          if (wipedCount > 0) {
            qbLog('WARNING: qb spans wiped after enhance!', wipedCount, 'labels affected');
            // Retry enhancement
            if (!this.isEnhancing) {
              qbLog('retrying enhancement due to wiped spans');
              this.enhanceQuantityBreaks();
            }
          } else if (emptyTextCount > 0) {
            qbLog('WARNING: text content empty after enhance!', emptyTextCount, 'elements empty');
            // Retry enhancement if text is empty
            if (!this.isEnhancing) {
              qbLog('retrying enhancement due to empty text');
              this.enhanceQuantityBreaks();
            }
          } else {
            qbLog('✓ qb spans still exist and populated after enhance');
          }
        }
      }, 150);

    } finally {
      this.isEnhancing = false;
      
      // Immediate retry check - if text is empty, retry once more
      setTimeout(() => {
        if (!this.isEnhancing) {
          const checkLabels = this.quantityOption?.querySelectorAll('.variant-option__button-label--quantity-break');
          if (checkLabels && checkLabels.length > 0) {
            const firstLabel = checkLabels[0];
            const titleEl = firstLabel?.querySelector('.qb__title');
            const unitEl = firstLabel?.querySelector('.qb__unit');
            
            // If text is empty, retry enhancement
            if ((titleEl && !titleEl.textContent.trim()) || (unitEl && !unitEl.textContent.trim())) {
              qbLog('text empty after enhance, retrying once');
              this.enhanceQuantityBreaks();
            }
          }
        }
      }, 100);
    }
  }

  setupSettleRunner() {
    if (!this.quantityOption) return;

    // Disconnect existing runner
    if (this.settleRunner) {
      this.settleRunner();
    }

    // Create new settle runner with isEnhancing check
    // This only triggers when labels are added/removed (Horizon morphing quantity container)
    this.settleRunner = createSettleRunner(
      this.quantityOption,
      () => {
        if (!this.isEnhancing) {
          qbLog('settle runner triggering enhance (quantity container morphed)');
          // Delay slightly to avoid interfering with any gallery updates
          setTimeout(() => {
            if (!this.isEnhancing) {
              this.loadData();
              this.enhanceQuantityBreaks();
            }
          }, 100);
        }
      },
      () => this.isEnhancing // Pass function to check if enhancing
    );
    
    qbLog('settle runner setup complete');
  }

  /**
   * Extract unit word from quantity option value (e.g. "1 Cup" → "Cup", "6 Pack" → "Pack").
   * Fallback to "Serving" if empty or not parseable.
   */
  getUnitFromOptionValue(valueStr) {
    if (!valueStr || typeof valueStr !== 'string') return 'حصة';
    const stripped = valueStr.replace(/^\d+\s*/i, '').trim();
    return stripped || 'حصة';
  }

  /** Singular form for subtitle. Arabic-only: fallback "حصة". */
  getUnitSingular(unitWord) {
    if (!unitWord || unitWord.length <= 1) return unitWord || 'حصة';
    if (unitWord.endsWith('s')) return unitWord.slice(0, -1);
    return unitWord;
  }

  /** True if option name is the quantity option (English, Arabic, or theme translation). */
  isQuantityOptionName(name) {
    if (!name || typeof name !== 'string') return false;
    const n = name.trim();
    const nLower = n.toLowerCase();
    if (this.quantityOptionNamesRaw.some((r) => r.trim() === n)) return true;
    if (this.quantityOptionNames.some((lc) => lc === nLower)) return true;
    return false;
  }

  findVariantForTier(tier) {
    // Get current selections for all other options
    const currentSelections = this.getCurrentOptionSelections();
    
    // Find variant that matches current selections + this tier's quantity value
    const quantityOptionData = this.productOptions.find((opt) => this.isQuantityOptionName(opt.name));
    
    if (!quantityOptionData) return null;

    // Find matching variant
    for (const variant of this.allVariants) {
      if (this.variantMatchesSelections(variant, currentSelections, tier.value)) {
        return variant;
      }
    }

    // Fallback: use variant ID from input if available
    if (tier.variantId) {
      return this.allVariants.find(v => v.id.toString() === tier.variantId.toString());
    }

    return null;
  }

  getCurrentOptionSelections() {
    const selections = {};
    
    // Get all selected inputs/options from the variant picker
    const selectedInputs = this.variantPicker.querySelectorAll(
      'input[type="radio"]:checked, select option[selected]'
    );

    selectedInputs.forEach(input => {
      const optionValueId = input.dataset?.optionValueId || 
                           (input instanceof HTMLOptionElement ? input.dataset?.optionValueId : null);
      
      if (optionValueId) {
        // Find which option this belongs to by position
        const inputId = input.dataset?.inputId || 
                       (input instanceof HTMLOptionElement ? input.dataset?.inputId : null);
        
        if (inputId) {
          const position = parseInt(inputId.split('-')[0]);
          
          for (const opt of this.productOptions) {
            if (opt.position === position) {
              const matchingValue = opt.values.find(v => v.id.toString() === optionValueId.toString());
              if (matchingValue) {
                selections[position] = matchingValue.value;
                break;
              }
            }
          }
        } else {
          // Fallback: find by option value ID
          for (const opt of this.productOptions) {
            const matchingValue = opt.values.find(v => v.id.toString() === optionValueId.toString());
            if (matchingValue) {
              selections[opt.position] = matchingValue.value;
              break;
            }
          }
        }
      }
    });

    return selections;
  }

  variantMatchesSelections(variant, selections, quantityValue) {
    // Get quantity option position
    const quantityOption = this.productOptions.find((opt) => this.isQuantityOptionName(opt.name));
    
    if (!quantityOption) return false;

    // Build selections map with quantity override
    const checkSelections = { ...selections };
    checkSelections[quantityOption.position] = quantityValue;

    // Match variant's option1/option2/option3 against selections by position
    const variantOptions = {};
    if (variant.option1 && variant.option1 !== 'Default Title') {
      variantOptions[1] = variant.option1;
    }
    if (variant.option2 && variant.option2 !== 'Default Title') {
      variantOptions[2] = variant.option2;
    }
    if (variant.option3 && variant.option3 !== 'Default Title') {
      variantOptions[3] = variant.option3;
    }

    // Check if all selections match variant options
    for (const [position, value] of Object.entries(checkSelections)) {
      const pos = parseInt(position);
      const variantValue = variantOptions[pos];
      
      if (variantValue && variantValue.toLowerCase() !== value.toLowerCase()) {
        return false;
      }
    }

    return true;
  }

  // Check if the change event is related to Quantity option
  isQuantityOptionChange(event) {
    if (!event || !event.target) return false;
    
    const target = event.target;
    // Check if the changed input is inside the quantity breaks container
    if (this.quantityOption && this.quantityOption.contains(target)) {
      return true;
    }
    
    // Check if it's a quantity option input by checking its container
    const input = target.closest ? target.closest('input[type="radio"], select') : null;
    if (input && this.quantityOption && this.quantityOption.contains(input)) {
      return true;
    }
    
    // Check if the change is to a quantity option by checking option position
    const quantityOptionData = this.productOptions.find((opt) => this.isQuantityOptionName(opt.name));
    if (quantityOptionData && input) {
      const inputId = input.dataset?.inputId;
      if (inputId) {
        const position = parseInt(inputId.split('-')[0]);
        if (position === quantityOptionData.position) {
          return true;
        }
      }
    }
    
    return false;
  }

  setupVariantChangeListener() {
    // Immediate enhancement function (for morphing) - ONLY for quantity changes
    const immediateEnhance = () => {
      if (this.isEnhancing) return;
      // Use requestAnimationFrame to run after DOM updates
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (!this.isEnhancing) {
            qbLog('immediate enhance triggered');
            this.loadData();
            this.enhanceQuantityBreaks();
          }
        }, 20);
      });
    };

    // Debounced enhancement for user interactions (to prevent rapid-fire) - ONLY for quantity changes
    const debouncedEnhance = (event) => {
      // Only enhance if this is a quantity option change
      if (!this.isQuantityOptionChange(event)) {
        qbLog('variant change ignored - not quantity option');
        return;
      }
      if (!this.isAutoSelectingTier) {
        this.userChangedQuantity = true;
      }
      
      if (this.enhanceTimeout) {
        clearTimeout(this.enhanceTimeout);
      }
      // Delay enhancement significantly to let Horizon finish ALL its updates (gallery, price, etc.)
      // This ensures we don't interfere with any of Horizon's native behavior
      // Gallery reload is expected when variant changes - we just wait for it to complete
      this.enhanceTimeout = setTimeout(() => {
        if (!this.isEnhancing) {
          qbLog('quantity option change triggering enhance (after all Horizon updates)');
          this.loadData();
          this.enhanceQuantityBreaks();
        }
      }, 400);
    };

    // Listen for native change events on variant picker
    // This will fire when user clicks labels (which triggers native input change)
    // BUT we only enhance if it's a quantity option change
    this.variantPicker.addEventListener('change', debouncedEnhance, { once: false });

    // Listen for Horizon's variant:update event (dispatched after morphing)
    // We enhance on variant:update because other option changes (flavor, etc.) affect quantity pricing
    // BUT: Only enhance if it's NOT a quantity option change (quantity changes are handled by change event)
    // This prevents double-enhancement and reduces interference with gallery
    this.variantPicker.addEventListener('variant:update', (event) => {
      // Check if quantity option container still exists
      const stillHasQuantityOption = this.variantPicker.querySelector('.variant-option--quantity-breaks');
      if (!stillHasQuantityOption || !this.quantityOption) {
        qbLog('variant:update ignored - no quantity option container');
        return;
      }
      
      // Check if this was a quantity option change (if so, we already handled it via change event)
      // We only need to enhance on variant:update if OTHER options changed (flavor, size, etc.)
      // because those affect quantity pricing but don't need immediate enhancement
      qbLog('variant:update event received - will enhance quantity breaks pricing after delay (other option changed)');
      // Delay enhancement significantly to let Horizon finish ALL updates (gallery, price, etc.)
      // This prevents any interference with gallery reload
      setTimeout(() => {
        if (!this.isEnhancing) {
          qbLog('enhancing quantity breaks pricing after variant update (other option)');
          this.loadData();
          this.enhanceQuantityBreaks();
        }
      }, 300);
    }, { once: false });

    // Listen for morphing completion by observing script tag changes
    // Only observe script tags, ignore our own DOM modifications
    this.scriptObserver = new MutationObserver((mutations) => {
      // Ignore if we're currently enhancing (to prevent feedback loop)
      if (this.isEnhancing) return;

      const hasScriptChanges = mutations.some(mutation => {
        const target = mutation.target;
        // Only react to script tag content changes, not attribute changes
        if (target && target.tagName === 'SCRIPT' && 
            (target.hasAttribute('data-all-variants') || 
             target.hasAttribute('data-product-options'))) {
          // Only trigger if it's a characterData change (content change), not childList
          return mutation.type === 'characterData' || 
                 (mutation.type === 'childList' && mutation.addedNodes.length > 0);
        }
        return false;
      });
      
      if (hasScriptChanges) {
        qbLog('script changes detected, settle runner will handle');
        // Settle runner will handle this
      }
    });

    this.scriptObserver.observe(this.variantPicker, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  formatMoney(cents) {
    const amount = cents / 100;
    try {
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (e) {
      // Fallback formatting
      return `$${amount.toFixed(2)}`;
    }
  }

  /** Format as "number currency" (e.g. "10.00 EGP") for subtitle. */
  formatMoneyAmountFirst(cents) {
    const amount = cents / 100;
    try {
      const formatted = new Intl.NumberFormat(this.locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
      return `${formatted} ${this.currency}`;
    } catch (e) {
      return `${amount.toFixed(2)} ${this.currency}`;
    }
  }

  destroy() {
    // Clean up observers and timeouts
    if (this.scriptObserver) {
      this.scriptObserver.disconnect();
      this.scriptObserver = null;
    }
    if (this.settleRunner) {
      this.settleRunner();
      this.settleRunner = null;
    }
    if (this.enhanceTimeout) {
      clearTimeout(this.enhanceTimeout);
      this.enhanceTimeout = null;
    }
    if (this.wipeCheckTimeout) {
      clearTimeout(this.wipeCheckTimeout);
      this.wipeCheckTimeout = null;
    }
    this.isEnhancing = false;
  }
}

// Initialize quantity breaks enhancement
function initQuantityBreaksEnhancement() {
  qbLog('initQuantityBreaksEnhancement() called');
  
  // Clean up existing instances first
  const allPickers = document.querySelectorAll('variant-picker');
  qbLog('Found', allPickers.length, 'variant-picker(s)');
  
  allPickers.forEach(picker => {
    const quantityOption = picker.querySelector('.variant-option--quantity-breaks');
    if (quantityOption && picker._quantityBreaksInstance) {
      // Clean up existing instance
      picker._quantityBreaksInstance.destroy();
      picker._quantityBreaksInstance = null;
    }
    delete picker.dataset.quantityBreaksInitialized;
  });

  // Initialize all uninitialized pickers
  const variantPickers = document.querySelectorAll('variant-picker:not([data-quantity-breaks-initialized])');
  qbLog('Found', variantPickers.length, 'uninitialized variant-picker(s)');
  
  variantPickers.forEach(picker => {
    const quantityOption = picker.querySelector('.variant-option--quantity-breaks');
    if (quantityOption) {
      qbLog('Initializing quantity breaks for variant-picker');
      picker.dataset.quantityBreaksInitialized = 'true';
      picker._quantityBreaksInstance = new QuantityBreaksEnhancement(picker);
    } else {
      qbLog('No quantity breaks container found in this variant-picker');
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initQuantityBreaksEnhancement);
} else {
  initQuantityBreaksEnhancement();
}

// Debounced initialization to prevent loops
let initTimeout = null;
const debouncedInit = () => {
  if (initTimeout) {
    clearTimeout(initTimeout);
  }
  initTimeout = setTimeout(() => {
    initQuantityBreaksEnhancement();
  }, 300);
};

// Also initialize on dynamic content updates (morphing)
// Only watch for new variant-picker elements, not all DOM changes
const observer = new MutationObserver((mutations) => {
  let shouldInit = false;
  
  mutations.forEach((mutation) => {
    // Only check added nodes, ignore removed/modified nodes
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType === 1) {
        // Check if a variant-picker was added
        if (node.tagName === 'VARIANT-PICKER') {
          shouldInit = true;
        } else if (node.querySelector && node.querySelector('variant-picker')) {
          shouldInit = true;
        }
      }
    });
    
    // Only check for script tag changes that indicate morphing
    if (mutation.type === 'childList' || mutation.type === 'characterData') {
      const target = mutation.target;
      if (target && target.tagName === 'SCRIPT' && 
          (target.hasAttribute('data-all-variants') || 
           target.hasAttribute('data-product-options'))) {
        // Only trigger if it's inside a variant-picker
        if (target.closest && target.closest('variant-picker')) {
          shouldInit = true;
        }
      }
    }
  });
  
  if (shouldInit) {
    debouncedInit();
  }
});

observer.observe(document.body, {
  childList: true,
  subtree: true
});

// Export for module usage
export default QuantityBreaksEnhancement;
